import React from 'react';
import { CheckCircle, Shield, Headphones } from 'lucide-react';

const PLAN_CONFIG = {
  Free: {
    badge: null,
    cardClass: 'bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300',
    textColor: 'text-green-900',
    priceColor: 'text-green-700',
    buttonClass: 'bg-green-600 hover:bg-green-700 text-white',
    features: ['100 leads/month', '500 emails', 'Community support']
  },
  Basic: {
    badge: null,
    cardClass: 'bg-white border border-gray-200 hover:border-blue-300',
    textColor: 'text-blue-900',
    priceColor: 'text-blue-700',
    buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    features: ['1k leads/month', '5k emails', 'AI Insights', 'Chat & Email support', '+ Many more features']
  },
  Professional: {
    badge: 'Most Popular',
    badgeGradient: 'from-blue-500 to-purple-600',
    cardClass: 'bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-500 ring-4 ring-blue-200 ring-opacity-30',
    textColor: 'text-blue-600',
    priceColor: 'text-blue-600',
    buttonClass: 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white',
    features: ['5k leads/month', '25k emails', 'AI Insights', 'Priority Chat & Email', '5 custom integrations', '+ Many more features']
  },
  Premium: {
    badge: 'Advanced',
    badgeGradient: 'from-purple-500 to-pink-600',
    cardClass: 'bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-400 ring-4 ring-purple-200 ring-opacity-30',
    textColor: 'text-purple-600',
    priceColor: 'text-purple-600',
    buttonClass: 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white',
    features: ['15k leads/month', '60k emails', 'AI Insights', 'Priority Support & Phone', '15 custom integrations', '+ Many more features']
  },
  Enterprise: {
    badge: 'Enterprise',
    badgeGradient: 'from-gray-700 to-gray-900',
    cardClass: 'bg-white border border-gray-200 hover:border-gray-400',
    textColor: 'text-gray-900',
    priceColor: 'text-gray-800',
    buttonClass: 'bg-gray-900 hover:bg-gray-800 text-white',
    features: ['25k leads/month', '100k emails', 'AI Insights', 'Dedicated Support Manager', 'Unlimited integrations', '99.9% SLA', '+ Many more features']
  }
};

const PricingPlansGrid = ({ plans = [], currentPlan, onSelectPlan }) => {
  // Normalize amount: DB may store as dollars (163) or cents (16300)
  // We display as dollars always
  const formatPrice = (amount) => {
    if (amount === null || amount === undefined || amount === 0) return null;
    // If amount > 1000, it's likely stored in cents
    const dollars = amount > 1000 ? Math.round(amount / 100) : Number(amount);
    return `$${dollars}`;
  };

  const isCurrentPlan = (plan) => {
    return plan?.name?.toLowerCase() === currentPlan?.name?.toLowerCase();
  };

  const getConfig = (planName) => PLAN_CONFIG?.[planName] || PLAN_CONFIG?.Basic;

  if (plans?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">No plans available</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {plans?.map((plan, index) => {
        const isCurrent = isCurrentPlan(plan);
        const config = getConfig(plan?.name);
        const price = formatPrice(plan?.amount);
        const features = config?.features || [];

        return (
          <div
            key={plan?.id || index}
            className={`relative p-5 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
              isCurrent ? 'ring-4 ring-offset-2 ring-blue-400' : ''
            } ${config?.cardClass}`}
          >
            {/* Current Plan Badge */}
            {isCurrent && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg whitespace-nowrap">
                  ✓ Current Plan
                </span>
              </div>
            )}

            {/* Popular/Advanced Badge */}
            {config?.badge && !isCurrent && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-lg border-2 border-white bg-gradient-to-r ${config?.badgeGradient} text-white whitespace-nowrap`}>
                  {config?.badge}
                </div>
              </div>
            )}

            <div className="text-center mb-4 pt-3">
              <h3 className={`text-lg font-bold mb-1 ${config?.textColor}`}>{plan?.name}</h3>
              <p className="text-gray-500 text-xs mb-2">{plan?.description || ''}</p>
              <div className="flex items-center justify-center space-x-1">
                {!price ? (
                  <span className={`text-2xl font-bold ${config?.priceColor}`}>Free</span>
                ) : (
                  <>
                    <span className={`text-2xl font-bold ${config?.priceColor}`}>{price}</span>
                    <span className="text-gray-500 text-xs">/mo</span>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Billed monthly</p>
            </div>

            <ul className="space-y-1.5 mb-4">
              {features?.map((feature, fi) => (
                <li key={fi} className="flex items-start space-x-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 text-xs">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              disabled={isCurrent}
              onClick={() => !isCurrent && onSelectPlan?.(plan)}
              className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all duration-300 shadow-md ${
                isCurrent
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : `${config?.buttonClass} hover:scale-105 cursor-pointer`
              }`}
            >
              {isCurrent ? 'Current Plan' : plan?.amount === 0 ? 'Start Free' : 'Upgrade Now'}
            </button>

            {(plan?.name === 'Professional' || plan?.name === 'Premium') && (
              <div className="mt-2 text-center">
                <div className="flex items-center justify-center space-x-2 text-xs text-gray-400">
                  <div className="flex items-center space-x-0.5">
                    <Shield className="w-3 h-3" />
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center space-x-0.5">
                    <Headphones className="w-3 h-3" />
                    <span>Support</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PricingPlansGrid;